import { Component, computed, effect, ElementRef, HostListener, inject, input, model, output, signal, viewChildren } from '@angular/core';
import { InternationalizationService, SessionService, User } from '@app/core/services';

@Component({
  selector: 'jeopardy-avatar-carousel',
  imports: [],
  templateUrl: './avatar-carousel.component.html',
  styleUrl: './avatar-carousel.component.scss'
})
export class AvatarCarouselComponent {
  private readonly selectedCenterOffsetPx = 30;

  showSaveCancelBar = input(false);

  #sessionService = inject(SessionService);
  i18n = inject(InternationalizationService);

  avatarElements = viewChildren<ElementRef<HTMLElement>>('avatar');
  avatarSelectedUpdate = output<string>();
  changeAvatarClearChange = output<boolean>();
  changeAvatarActive = model<boolean>();

  avatarIndex = signal(0);
  avatars = signal<string[]>([...Array(18).keys()].map(e => `assets/app-branding/img/avatars/cartoon/${e + 1}.png`));
  avatarSelected = computed(() => this.avatars()[this.avatarIndex()]);
  currentUser = signal<User>(this.#sessionService.getUser());

  constructor() {
    effect(() => {
      this.centerSelectedAvatar();
    });
    effect(() => {
      if (!this.changeAvatarActive()) {
        this.avatarSelectedUpdate.emit(this.avatarSelected());
      }
    });
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.centerSelectedAvatar();
  }

  private centerSelectedAvatar() {
    const elements = this.avatarElements();
    const selectedElement = elements[this.avatarIndex()]?.nativeElement;
    const wrapper = selectedElement?.parentElement;

    if (!selectedElement || !wrapper || wrapper.clientWidth === 0) {
      return;
    }

    const selectedCenter = selectedElement.offsetLeft + selectedElement.offsetWidth / 2;
    const wrapperCenter = wrapper.clientWidth / 2;
    const translateX = wrapperCenter - selectedCenter + this.selectedCenterOffsetPx;

    elements.forEach(el => {
      el.nativeElement.style.transform = `translateX(${translateX}px)`;
    });
  }

  skipAvatar(ev: Event, orientation: 'left' | 'right') {
    ev.preventDefault();

    if (orientation === 'left') {
      this.avatarIndex.update((a) => Math.max(0, a - 1));
    } else {
      this.avatarIndex.update((a) => Math.min(this.avatars().length - 1, a + 1));
    }
  }

  pickAvatar(index: number) {
    this.avatarIndex.set(index)
  }

  saveNewAvatar(ev: Event) {
    ev.preventDefault();

    this.currentUser.update((user) => {
      user ??= this.#sessionService.getUser();
      user.avatar = this.avatarSelected();
      return user;
    });

    this.#sessionService.setUser(this.currentUser(), false);
    this.changeAvatarActive.set(false);
    this.changeAvatarClearChange.emit(false);
  };

  cancelAvatarChange(ev: Event) {
    ev.preventDefault();
    this.changeAvatarActive.set(false);
    this.changeAvatarClearChange.emit(false);
  };
}
